import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import { ReactComponent as Comprehensive } from "../../../images/Comprehensive.svg";
import { ReactComponent as Detailed } from "../../../images/Detailed.svg";
import { ReactComponent as Risk } from "../../../images/Risk.svg";
import { ReactComponent as Security } from "../../../images/Security.svg";
import { ReactComponent as Automated } from "../../../images/automated-icon.svg";
import { ReactComponent as Smart } from "../../../images/smart-icon.svg";
import { ReactComponent as DetailedIssue } from "../../../images/DetailedIssue-Icon.svg";
import { ReactComponent as Graph } from "../../../images/Continous-icon.svg";
import { burpSuiteScanAPI, dastScanAPI } from "../../../services/webServices";
import { useNavigate } from "react-router-dom";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import {
  SecurityFlowDataContext,
  SecurityLoaderStateContext,
} from "../../main-pages/SecurityTestingPage";
import SessionExpired from "../../popup-components/SessionExpiredPopup";

export default function DASTInputPage() {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [scanner, setScanner] = useState("QualiSec");
  const [errorMessage, setErrorMessage] = useState("");
  const [sessionExpired, setSessionExpired] = useState(false);
  const [selectRunType, setSelectRunType] = useState("");
  const { setLoadingState } = useContext(LoadingModalContext);
  const { setSecurityLoaderState } = useContext(SecurityLoaderStateContext);
  const { setDastScanResult } = useContext(SecurityFlowDataContext);

  const handleStartScan = async () => {
    let fake;

    const startProgress = (setProgress) => {
      let progress = 0;
      let stop = false;
      let finalMessageShown = false;

      const interval = setInterval(() => {
        if (stop) return;

        progress += 0.5;

        if (progress >= 97 && !finalMessageShown) {
          finalMessageShown = true;
          setLoadingState((prev) => ({
            ...prev,
            message: "Almost done! Fetching your output",
          }));
        }

        if (progress >= 97) progress = 97;

        setProgress((prev) => ({
          ...prev,
          progress: Math.max(prev.progress, Math.floor(progress)),
        }));
      }, 200);

      return {
        stop: () => {
          stop = true;
          clearInterval(interval);
        },
        completeTo100: async () => {
          stop = true;
          clearInterval(interval);
          setProgress((prev) => ({
            ...prev,
            progress: 100,
          }));
          await new Promise((res) => setTimeout(res, 500));
        },
      };
    };

    const finishTo100AndClose = async () => {
      await fake.completeTo100();
      setLoadingState({ open: false, message: "", progress: null });
    };

    // Initial loading state
    setLoadingState({
      open: true,
      message:
        scanner === "QualiSec"
          ? "Running DAST Passive Scan"
          : "Running DAST Active Scan",
      progress: 0,
    });

    fake = startProgress(setLoadingState);

    try {
      let finalUrl = url; // ✅

      setSecurityLoaderState("DAST Scan");

      if (!finalUrl) {
        setLoadingState({ open: false, message: "", progress: null });
        setErrorMessage("Please enter a valid URL.");
        return;
      }

      const strictIPPattern =
        /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

      const strippedUrl = finalUrl.replace(/^https?:\/\//, "").split("/")[0];

      if (strictIPPattern.test(strippedUrl)) {
        if (
          !finalUrl.startsWith("http://") &&
          !finalUrl.startsWith("https://")
        ) {
          finalUrl = `http://${finalUrl}`;
        }
      }

      if (!finalUrl.startsWith("http://") && !finalUrl.startsWith("https://")) {
        setLoadingState({ open: false, message: "", progress: null });
        setErrorMessage("Please enter a valid HTTPS or HTTP URL.");
        return;
      }

      if (scanner === "QualiSec") {
        const scanResponse = await dastScanAPI({ url: finalUrl });
        setDastScanResult(scanResponse.data.transformedReport);
        navigate("/dashboard/security-testing/dast-phase", {
          state: {
            dastScanResult: scanResponse.data.transformedReport,
            selectedScanner: "QualiSec",
            webURL: finalUrl,
            runType: selectRunType,
          },
        });
      }

      if (scanner === "Burp Suite") {
        const scanResponse = await burpSuiteScanAPI({
          url: finalUrl,
          username: username,
          password: password,
        });
        setDastScanResult(scanResponse.data);
        // console.log("Burp Suite scan response:", scanResponse);
        await finishTo100AndClose();
        navigate("/dashboard/security-testing/burpsuite-scan-dashboard", {
          state: {
            selectedScanner: "BurpSuite",
            webURL: finalUrl,
          },
        });
      }

      setLoadingState({ open: false, message: "", progress: null });
    } catch (error) {
      console.log("Error starting scan:", error.response?.data?.message);
      setLoadingState({ open: false, message: "", progress: null });

      if (error.response?.status === 401) {
        setSessionExpired(true);
      } else {
        setErrorMessage(
          error.response?.data?.message ||
            "An error occurred while starting the scan."
        );
      }
    }

    setLoadingState({ open: false, message: "", progress: null });
  };

  const cards = [
    {
      icon: (
        <Comprehensive
          color="primary"
          sx={{ fontSize: 48 }}
          style={{ height: 50, width: 190 }}
        />
      ),
      title: "Comprehensive Analysis",
      description:
        "Scan for SQL injection, XSS, and other common vulnerabilities",
    },
    {
      icon: (
        <Security
          color="warning"
          sx={{ fontSize: 48 }}
          style={{ height: 50, width: 190 }}
        />
      ),
      title: "Security Headers",
      description: "Verify proper implementation of security headers",
    },
    {
      icon: (
        <Detailed
          color="secondary"
          sx={{ fontSize: 48 }}
          style={{ height: 50, width: 190 }}
        />
      ),
      title: "Detailed Reports",
      description: "Get actionable insights with severity ratings",
    },
    {
      icon: (
        <Risk
          color="success"
          sx={{ fontSize: 48 }}
          style={{ height: 55, width: 190 }}
        />
      ),
      title: "Risk Assessment",
      description: "Prioritize vulnerabilities based on CVSS scores",
    },
  ];

  // Active scanning cards
  const activeCards = [
    {
      icon: (
        <Automated
          color="primary"
          sx={{ fontSize: 48 }}
          style={{ height: 50, width: 190 }}
        />
      ),
      title: "Automated Vulnerability Detection",
      description:
        "Identify issues like SQL injection, XSS, SSRF, and other OWASP Top 10 vulnerabilities.",
    },
    {
      icon: (
        <Smart
          color="warning"
          sx={{ fontSize: 48 }}
          style={{ height: 50, width: 190 }}
        />
      ),
      title: "Smart Scanning Engine",
      description:
        "Use heuristics and behavior analysis to detect complex vulnerabilities intelligently.",
    },
    {
      icon: (
        <DetailedIssue
          color="secondary"
          sx={{ fontSize: 48 }}
          style={{ height: 50, width: 190 }}
        />
      ),
      title: "Detailed Issue Reports",
      description:
        "View categorized issues with severity ratings, remediation advice for each finding.",
    },
    {
      icon: (
        <Graph
          color="success"
          sx={{ fontSize: 48 }}
          style={{ height: 55, width: 190 }}
        />
      ),
      title: "Continuous Passive + Active Scanning",
      description:
        "Combine passive monitoring with aggressive probing to uncover hidden issues.",
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        minHeight: "100vh",
        px: { xs: 1.5, sm: 2, md: 3 }, // responsive horizontal padding
        py: { xs: 1.5, sm: 2 }, // top/bottom breathing space
      }}
    >
      <Box
        sx={{
          width: "100%",
          // keep original max look but guide smaller screens smoothly
          maxWidth: {
            xs: "100%",
            sm: "900px",
            md: "1200px",
            lg: "1600px",
            xl: "2300px",
          },
          mx: "auto",
        }}
      >
        <Card
          sx={{
            width: "100%",
            mt: { xs: 2, md: 4 },
            p: { xs: 2, sm: 3, md: 4 },
            boxSizing: "border-box",
          }}
        >
          {/* Header */}
          <Box sx={{ mb: { xs: 1.5, sm: 2 }, textAlign: "center" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: { xs: 0.5, sm: 1 },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  // scale title down on small screens without changing style
                  fontSize: { xs: "24px", sm: "30px", md: "38px" },
                  mb: { xs: 0.5, sm: 1 },
                  lineHeight: 1.2,
                }}
              >
                Web Application Vulnerability Scanner
              </Typography>
            </Box>
          </Box>

          <CardContent sx={{ p: 0 }}>
            <Typography
              sx={{
                display: "flex",
                fontWeight: 600,
                fontSize: { xs: "16px", sm: "17px", md: "18px" },
                mb: 1,
              }}
            >
              Target URL or IP Address
            </Typography>

            <TextField
              fullWidth
              placeholder="https://example.com or 192.168.1.1"
              variant="outlined"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1 }} />,
              }}
              sx={{
                mb: { xs: 1.5, sm: 2 },
                borderRadius: "12px",
              }}
            />

            {/* Select Run */}
            <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
              <Typography
                sx={{
                  display: "flex",
                  fontWeight: 600,
                  fontSize: { xs: "16px", sm: "17px", md: "18px" },
                  mb: 1,
                }}
              >
                Select Run-1 or Run-2
              </Typography>

              <FormControl
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  width: "100%",
                  height: { xs: "48px", sm: "56px" },
                  mb: { xs: 2, sm: 3 },
                }}
              >
                <Select
                  labelId="automation-test"
                  value={selectRunType}
                  onChange={(e) => setSelectRunType(e.target.value)}
                  displayEmpty
                  fullWidth
                  sx={{ height: { xs: "48px", sm: "56px" } }}
                  renderValue={(selected) =>
                    selected ? (
                      selected
                    ) : (
                      <span style={{ color: "#999" }}>Select run type</span>
                    )
                  }
                >
                  <MenuItem disabled value="">
                    <em>Select Run type</em>
                  </MenuItem>
                  <MenuItem value="Run-1">Run-1</MenuItem>
                  <MenuItem value="Run-2">Run-2</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {errorMessage && (
              <Typography color="error" sx={{ mb: { xs: 1.5, sm: 2 } }}>
                {errorMessage}
              </Typography>
            )}

            <Typography
              sx={{
                display: "flex",
                fontWeight: 600,
                fontSize: { xs: "16px", sm: "17px", md: "18px" },
                mb: { xs: 1, sm: 2 },
              }}
            >
              Select Scanner
            </Typography>

            {/* Scanner options: stack on mobile, row on md+ */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 1.5, sm: 2 },
                mb: { xs: 1.5, sm: 2 },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <ToggleButtonGroup
                  value={scanner}
                  exclusive
                  onChange={(e, newScanner) => {
                    if (newScanner !== null) setScanner(newScanner);
                  }}
                  fullWidth
                  sx={{ height: { xs: "auto", sm: "128px" } }}
                  orientation={undefined}
                >
                  <ToggleButton
                    value="QualiSec"
                    sx={{
                      flex: 1,
                      py: { xs: 1.5, sm: 0 },
                      "&.Mui-selected": {
                        backgroundColor: "#e8f4fd",
                        border: "2px solid #90caf9",
                        "&:hover": { backgroundColor: "#e8f4fd" },
                      },
                      "&:not(.Mui-selected)": {
                        backgroundColor: "#ffffff",
                        border: "2px solid #e0e0e0",
                        "&:hover": { backgroundColor: "#f5f5f5" },
                      },
                    }}
                  >
                    <Box sx={{ textAlign: "left" }}>
                      <Typography
                        fontWeight="bold"
                        sx={{
                          textTransform: "capitalize",
                          fontSize: { xs: "14px", sm: "16px" },
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        For Quick Passive Scan - QualiSec
                      </Typography>
                      <Typography
                        sx={{
                          textTransform: "none",
                          fontSize: { xs: "13px", sm: "14px" },
                          fontWeight: 400,
                        }}
                      >
                        Identifies and reveal risks associated with publicly
                        available pages
                      </Typography>
                    </Box>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box sx={{ flex: 1 }}>
                <ToggleButtonGroup
                  value={scanner}
                  exclusive
                  onChange={(e, newScanner) => {
                    if (newScanner !== null) setScanner(newScanner);
                  }}
                  fullWidth
                  sx={{ height: { xs: "auto", sm: "128px" } }}
                >
                  <ToggleButton
                    value="Burp Suite"
                    sx={{
                      flex: 1,
                      py: { xs: 1.5, sm: 0 },
                      "&.Mui-selected": {
                        backgroundColor: "#e8f4fd",
                        border: "2px solid #90caf9",
                        "&:hover": { backgroundColor: "#e8f4fd" },
                      },
                      "&:not(.Mui-selected)": {
                        backgroundColor: "#ffffff",
                        border: "2px solid #e0e0e0",
                        "&:hover": { backgroundColor: "#f5f5f5" },
                      },
                    }}
                  >
                    <Box sx={{ textAlign: "left" }}>
                      <Typography
                        fontWeight="bold"
                        sx={{
                          textTransform: "capitalize",
                          fontSize: { xs: "14px", sm: "16px" },
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        For Active Scan - QualiSec Advanced
                      </Typography>
                      <Typography
                        sx={{
                          textTransform: "none",
                          fontSize: { xs: "13px", sm: "14px" },
                          fontWeight: 400,
                        }}
                      >
                        Includes authenticated/comprehensive scanning
                      </Typography>
                    </Box>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

            {/* Auth notice for Burp Suite */}
            {scanner === "Burp Suite" && (
              <>
                <Box sx={{ display: "flex", flexDirection: "column", mt: 3 }}>
                  <Box
                    component="ul"
                    p={1}
                    pl={2}
                    sx={{
                      backgroundColor: "#F0F9FF",
                      borderRadius: "8px",
                      listStyleType: "disc",
                      listStylePosition: "inside",
                      borderLeft: "8px solid #0094FB",
                      lineHeight: 1.6,
                      fontSize: { xs: "13px", sm: "14px" },
                    }}
                  >
                    For authenticated scanning, please provide the target web
                    application credentials.
                  </Box>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: "14px", sm: "15px" },
                      fontWeight: 600,
                    }}
                  >
                    Enter User Name*
                  </Typography>
                  <TextField
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    fullWidth
                    placeholder="Enter username"
                    variant="outlined"
                    sx={{ mt: 1, mb: 2, borderRadius: "12px" }}
                  />

                  <Typography
                    sx={{
                      fontSize: { xs: "14px", sm: "15px" },
                      fontWeight: 600,
                    }}
                  >
                    Enter Password*
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter password"
                    variant="outlined"
                    type="password"
                    sx={{ mt: 1, mb: 2, borderRadius: "12px" }}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Box>
              </>
            )}

            {/* Action buttons */}
            {scanner === "QualiSec" && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<GenerateButton />}
                sx={{
                  mt: { xs: 1, sm: 0 },
                  alignSelf: { xs: "stretch", sm: "flex-end" },
                  float: { sm: "right" },
                  width: { xs: "100%", sm: "140px" },
                  height: "38px",
                  textTransform: "none",
                  backgroundColor: "#49A3FF",
                  fontSize: { xs: "13px", sm: "14px" },
                  fontWeight: 500,
                }}
                disabled={!url || !selectRunType || !scanner}
                onClick={handleStartScan}
              >
                Start Scan
              </Button>
            )}

            {scanner === "Burp Suite" && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: { xs: "stretch", sm: "right" },
                  gap: { xs: 1, sm: 2 },
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    height: "38px",
                    textTransform: "none",
                    backgroundColor: "#49A3FF",
                    fontSize: { xs: "13px", sm: "14px" },
                    fontWeight: 500,
                    mr: { sm: 4, md: 4 },
                    whiteSpace: "nowrap",
                    width: { xs: "100%", sm: "auto" },
                  }}
                  onClick={() =>
                    navigate(
                      "/dashboard/security-testing/burpsuite-scan-dashboard"
                    )
                  }
                >
                  View Scan History
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<GenerateButton />}
                  sx={{
                    width: { xs: "100%", sm: "140px" },
                    height: "38px",
                    textTransform: "none",
                    backgroundColor: "#49A3FF",
                    fontSize: { xs: "13px", sm: "14px" },
                    fontWeight: 500,
                  }}
                  disabled={
                    !url || !selectRunType || !scanner || !username || !password
                  }
                  onClick={handleStartScan}
                >
                  Start Scan
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Features */}
        {scanner === "QualiSec" && (
          <Box sx={{ mt: { xs: 3, md: 4 }, mb: { xs: 4, md: 6 } }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                mb: { xs: 2, sm: 3 },
                textAlign: "center",
                fontSize: { xs: "20px", sm: "22px", md: "24px" },
                fontWeight: 700,
              }}
            >
              Passive Scanning Features
            </Typography>

            <Grid
              container
              spacing={{ xs: 2, sm: 3 }}
              justifyContent="center"
              alignItems="stretch"
            >
              {cards.map((card, index) => (
                <Grid key={index} item xs={12} sm={6} md={4} lg={3} xl={3}>
                  <Card sx={{ height: "100%" }}>
                    <CardContent
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        height: "100%",
                        p: { xs: 2, sm: 3 },
                        "&:last-child": { pb: 3 },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          mb: 2,
                          minHeight: 63,
                        }}
                      >
                        {card.icon}
                      </Box>

                      <Typography
                        variant="body1"
                        sx={{
                          mb: 1,
                          minHeight: 24,
                          fontSize: { xs: "13px", sm: "14px" },
                          fontWeight: 600,
                        }}
                      >
                        {card.title}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          minHeight: 52,
                          lineHeight: 1.6,
                          fontSize: { xs: "12.5px", sm: "13px" },
                          fontWeight: 400,
                        }}
                      >
                        {card.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {scanner === "Burp Suite" && (
          <Box sx={{ mt: { xs: 3, md: 4 }, mb: { xs: 4, md: 6 } }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                mb: { xs: 2, sm: 3 },
                textAlign: "center",
                fontSize: { xs: "20px", sm: "22px", md: "24px" },
                fontWeight: 700,
              }}
            >
              Active Scanning Features
            </Typography>

            <Grid
              container
              spacing={{ xs: 2, sm: 3 }}
              justifyContent="center"
              alignItems="stretch"
            >
              {activeCards.map((card, index) => (
                <Grid key={index} item xs={12} sm={6} md={4} lg={3} xl={3}>
                  <Card sx={{ height: "100%" }}>
                    <CardContent
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        height: "100%",
                        p: { xs: 2, sm: 3 },
                        "&:last-child": { pb: 3 },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          mb: 2,
                          minHeight: 63,
                        }}
                      >
                        {card.icon}
                      </Box>

                      <Typography
                        variant="body1"
                        sx={{
                          mb: 1,
                          minHeight: 24,
                          fontSize: { xs: "13px", sm: "14px" },
                          fontWeight: 600,
                        }}
                      >
                        {card.title}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          minHeight: 52,
                          lineHeight: 1.6,
                          fontSize: { xs: "12.5px", sm: "13px" },
                          fontWeight: 400,
                        }}
                      >
                        {card.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>

      {sessionExpired && <SessionExpired />}
    </Box>
  );
}
